/**
 * The read-only status chip: 4px shouldered, tracked uppercase caption, and one colour per claim.
 *
 * `secondary` stays the base so colour keeps meaning — a screen where every badge is coloured has
 * no badge that means anything. What the restyle changed is what the coloured ones ARE: reward is
 * GOLD and only gold in both looks, so `success` and `warning` are one treatment rather than two
 * (nothing in this app is a warning that is not also "not yet"), and `default` takes --primary,
 * because the boards have no subtle-highlight fill left to give it.
 *
 * `band` is the new one, and it is the only variant that knows where it is: a badge riding ON a
 * header band, drawn as the sheets draw it — a 70% white outline with white type, on a panel that
 * is red in Bouquet and midnight in Tuxedo, so the same two values are right in all four rooms.
 */
import { TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { Slot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Platform, View } from 'react-native';

const badgeVariants = cva(
  cn(
    'border-border group shrink-0 flex-row items-center justify-center gap-1 self-start overflow-hidden rounded-sm border px-2 py-0.5',
    Platform.select({
      web: 'focus-visible:border-ring focus-visible:ring-ring/50 w-fit whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px]',
    }),
  ),
  {
    variants: {
      variant: {
        default: 'bg-primary border-transparent',
        secondary: 'bg-secondary border-secondary-line',
        outline: 'border-line-strong bg-transparent',
        destructive: 'bg-destructive/15 border-transparent',
        success: 'bg-gold/15 border-transparent',
        warning: 'bg-gold/15 border-transparent',
        band: 'border-white/70 bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'secondary',
    },
  },
);

const badgeTextVariants = cva('text-caption uppercase', {
  variants: {
    variant: {
      default: 'text-primary-foreground',
      secondary: 'text-secondary-foreground',
      outline: 'text-muted-foreground',
      destructive: 'text-destructive',
      success: 'text-gold-text',
      warning: 'text-gold-text',
      band: 'text-white',
    },
  },
  defaultVariants: {
    variant: 'secondary',
  },
});

/** The dot cannot be `currentColor` — React Native has no such thing — so it repeats the variant. */
const badgeDotVariants = cva('size-1.5 rounded-[2px]', {
  variants: {
    variant: {
      default: 'bg-primary-foreground',
      secondary: 'bg-secondary-foreground',
      outline: 'bg-muted-foreground',
      destructive: 'bg-destructive',
      success: 'bg-gold-text',
      warning: 'bg-gold-text',
      band: 'bg-white',
    },
  },
  defaultVariants: {
    variant: 'secondary',
  },
});

type BadgeProps = React.ComponentProps<typeof View> &
  React.RefAttributes<View> & {
    asChild?: boolean;
    /** A status that reads at a glance before the word does. */
    dot?: boolean;
  } & VariantProps<typeof badgeVariants>;

function Badge({ className, variant, asChild, dot, children, ...props }: BadgeProps) {
  const Component = asChild ? Slot : View;
  return (
    <TextClassContext.Provider value={badgeTextVariants({ variant })}>
      <Component className={cn(badgeVariants({ variant }), className)} {...props}>
        {dot ? <View className={badgeDotVariants({ variant })} /> : null}
        {children}
      </Component>
    </TextClassContext.Provider>
  );
}

export { Badge, badgeDotVariants, badgeTextVariants, badgeVariants };
export type { BadgeProps };
